import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PdfBrush, PdfColor } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';

/**
 * Octagon - Octagonal tile puzzle with interlocking shapes
 *
 * Layout: 2 portrait pages.
 * Page 1: 3×4 grid of octagons with images inside.
 * Page 2: outline showing how the octagons tessellate.
 *
 * Migrated from C# EduFrame.Img10.Generators.Octagon
 */
export class Octagon implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 8;
  }
  countMax(): number {
    return 12;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const a = (PdfSize.A4.width - 2 * margin) / 3 / (1 + Math.SQRT2);
    const c = a / Math.SQRT2;
    const origin = new PdfPoint(margin, margin);

    const page1 = pdf.addPage();
    const page2 = pdf.addPage();

    const L = (ac: number, cc: number) => ac * a + cc * c;
    const P = (ac1: number, cc1: number, ac2: number, cc2: number) =>
      new PdfPoint(origin.x + L(ac1, cc1), origin.y + L(ac2, cc2));

    const background = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1));

    const allTexts = opts.list.slice(0, 12).map((item) => item.left.getText());
    const cellSize = new PdfSize(L(1, 1), L(1, 1));
    const labelFont = page1.adjustFontMulti(
      allTexts.map((t) => [t]),
      GUtil.baseFont,
      cellSize
    );

    // Draw octagons on page 1
    for (let x = 0; x < 3; x++) {
      for (let y = 0; y < 4; y++) {
        const index = x * 4 + y;
        if (index >= opts.list.length) continue;

        const points: PdfPoint[] = [
          P(0, 1, 0, 0),
          P(1, 1, 0, 0),
          P(1, 2, 0, 1),
          P(1, 2, 1, 1),
          P(1, 1, 1, 2),
          P(0, 1, 1, 2),
          P(0, 0, 1, 1),
          P(0, 0, 0, 1),
          P(0, 1, 0, 0),
        ].map((p) => new PdfPoint(p.x + x * L(1, 2), p.y + y * L(1, 2)));

        const r = new PdfRect(
          origin.x + x * L(1, 2) + c / 2,
          origin.y + y * L(1, 2) + c / 2,
          L(1, 1),
          L(1, 1)
        );

        page1.drawPolygon(null, background, points.slice(0, 8));
        page1.drawLabel(opts.list[index].left, { rect: r, font: labelFont });
        page1.drawLines(GUtil.cutPen, points);
      }
    }

    // Draw tessellation outline on page 2
    const outline: PdfPoint[] = [
      P(0, 1, 0, 1),
      P(1, 1, 0, 1),
      P(1, 2, 0, 0),
      P(1, 3, 0, 1),
      P(1, 3, 0, 1),
      P(2, 3, 0, 1),
      P(2, 3, 1, 1),
      P(2, 4, 1, 2),
      P(2, 3, 1, 3),
      P(2, 3, 2, 3),
      P(1, 3, 2, 3),
      P(1, 2, 2, 4),
      P(1, 1, 2, 3),
      P(0, 1, 2, 3),
      P(0, 1, 1, 3),
      P(0, 0, 1, 2),
      P(0, 1, 1, 1),
      P(0, 1, 0, 1),
    ].map((p) => new PdfPoint(origin.x + p.x, origin.y + p.y));

    page2.drawLines(GUtil.basePen, outline);
  }
}
